"use client"

import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { useCallback, useEffect, useState } from "react"
import useEmblaCarousel from "embla-carousel-react"
import Autoplay from "embla-carousel-autoplay"
import { getReferences } from "@/lib/cms/queries"
import type { Reference } from "@/types/database"
import { stripHTML } from "@/lib/utils"
import { getTranslations } from "@/lib/translations"
import type { Locale } from "@/lib/i18n"

// Slug mapping: English slugs -> Turkish slugs
const slugToTurkish: Record<string, string> = {
  'web-design': 'web-tasarim',
  'web-development': 'web-yazilim',
  'ui-ux-design': 'ui-ux-tasarim',
  'mobile-app': 'mobil-uygulama',
  'desktop-app': 'masaustu-uygulama',
  'e-commerce': 'e-ticaret',
  'shopify': 'shopify',
  'woocommerce': 'woocommerce',
  'automation': 'otomasyon',
  'ai-services': 'ai-servisler',
}

// Hizmet slug'larını kategori isimlerine map et (hem Türkçe hem İngilizce)
const serviceCategoryMap: Record<string, string[]> = {
  'web-tasarim': ['Web Tasarım', 'Web Design'],
  'web-design': ['Web Tasarım', 'Web Design'],
  'web-yazilim': ['Web Yazılım', 'Web Development'],
  'web-development': ['Web Yazılım', 'Web Development'],
  'ui-ux-tasarim': ['UI & UX Tasarım', 'UI & UX Design'],
  'ui-ux-design': ['UI & UX Tasarım', 'UI & UX Design'],
  'mobil-uygulama': ['Mobil Uygulama', 'Mobile App'],
  'mobile-app': ['Mobil Uygulama', 'Mobile App'],
  'masaustu-uygulama': ['Masaüstü Uygulama', 'Desktop App'],
  'desktop-app': ['Masaüstü Uygulama', 'Desktop App'],
  'e-ticaret': ['E-ticaret', 'E-ticaret Sistemleri', 'E-Commerce', 'E-Commerce Systems'],
  'e-commerce': ['E-ticaret', 'E-ticaret Sistemleri', 'E-Commerce', 'E-Commerce Systems'],
  'shopify': ['Shopify'],
  'woocommerce': ['WooCommerce'],
  'otomasyon': ['Otomasyon', 'Automation'],
  'automation': ['Otomasyon', 'Automation'],
  'ai-servisler': ['AI Servisler', 'AI Ürünleştirilmiş Servisler', 'Yapay Zeka', 'AI Services', 'AI Productized Services'],
  'ai-services': ['AI Servisler', 'AI Ürünleştirilmiş Servisler', 'Yapay Zeka', 'AI Services', 'AI Productized Services'],
}

// Hizmet slug'larını hizmet adlarına map et (Türkçe)
const serviceNameMapTR: Record<string, string> = {
  'web-tasarim': 'Web Tasarım',
  'web-design': 'Web Tasarım',
  'web-yazilim': 'Web Yazılım',
  'web-development': 'Web Yazılım',
  'ui-ux-tasarim': 'UI & UX Tasarım',
  'ui-ux-design': 'UI & UX Tasarım',
  'mobil-uygulama': 'Mobil Uygulama',
  'mobile-app': 'Mobil Uygulama',
  'masaustu-uygulama': 'Masaüstü Uygulama',
  'desktop-app': 'Masaüstü Uygulama',
  'e-ticaret': 'E-ticaret',
  'e-commerce': 'E-ticaret',
  'shopify': 'Shopify',
  'woocommerce': 'WooCommerce',
  'otomasyon': 'Otomasyon',
  'automation': 'Otomasyon',
  'ai-servisler': 'AI Servisler',
  'ai-services': 'AI Servisler',
}

// Hizmet slug'larını hizmet adlarına map et (İngilizce)
const serviceNameMapEN: Record<string, string> = {
  'web-tasarim': 'Web Design',
  'web-design': 'Web Design',
  'web-yazilim': 'Web Development',
  'web-development': 'Web Development',
  'ui-ux-tasarim': 'UI & UX Design',
  'ui-ux-design': 'UI & UX Design',
  'mobil-uygulama': 'Mobile App',
  'mobile-app': 'Mobile App',
  'masaustu-uygulama': 'Desktop App',
  'desktop-app': 'Desktop App',
  'e-ticaret': 'E-Commerce',
  'e-commerce': 'E-Commerce',
  'shopify': 'Shopify',
  'woocommerce': 'WooCommerce',
  'otomasyon': 'Automation',
  'automation': 'Automation',
  'ai-servisler': 'AI Services',
  'ai-services': 'AI Services',
}

interface ServiceReferencesProps {
  serviceSlug: string
  locale?: Locale
}

export function ServiceReferences({ serviceSlug, locale = 'tr' }: ServiceReferencesProps) {
  const [referencesData, setReferencesData] = useState<Reference[]>([])
  const [loading, setLoading] = useState(true)
  const t = getTranslations(locale)
  
  const serviceNameMap = locale === 'en' ? serviceNameMapEN : serviceNameMapTR
  const serviceName = serviceNameMap[serviceSlug] || (locale === 'en' ? 'This Service' : 'Bu Hizmet')

  useEffect(() => {
    loadReferences()
  }, [serviceSlug, locale])

  const loadReferences = async () => {
    try {
      // Tüm published referansları getir (locale filtresi ile)
      const refs = await getReferences('published', locale)
      
      // Hizmete ait kategorileri al
      const serviceCategories = serviceCategoryMap[serviceSlug] || []
      
      if (serviceCategories.length === 0) {
        setReferencesData([])
        setLoading(false)
        return
      }

      // Kategori normalizasyon fonksiyonu (referanslar sayfasındaki ile aynı)
      const normalizeCategory = (cat: string): string => {
        const lower = cat.toLowerCase().trim()
        
        if (lower.includes('woocommerce')) {
          return 'WooCommerce'
        }
        if (lower.includes('shopify')) {
          return 'Shopify'
        }
        if (lower.includes('e-ticaret') || lower === 'e-ticaret sistemleri' || lower.includes('e-commerce') || lower === 'e-commerce systems') {
          return 'E-ticaret'
        }
        if (lower.includes('ai') || lower.includes('yapay zeka')) {
          return 'AI Servisler'
        }
        if (lower.includes('otomasyon') || lower.includes('automation') || lower.includes('n8n')) {
          return 'Otomasyon'
        }
        if (lower.includes('ui & ux') || lower.includes('ui/ux')) {
          return 'UI & UX Tasarım'
        }
        if (lower.includes('web design') || lower.includes('web tasarım')) {
          return 'Web Tasarım'
        }
        if (lower.includes('web development') || lower.includes('web programlama') || lower.includes('web yazılım')) {
          return 'Web Yazılım'
        }
        if (lower.includes('mobile app') || lower.includes('mobil uygulama')) {
          return 'Mobil Uygulama'
        }
        if (lower.includes('desktop app') || lower.includes('masaustu uygulama')) {
          return 'Masaüstü Uygulama'
        }
        
        return cat
      }

      // Referansları filtrele - kategori eşleşmesi varsa
      const filteredRefs = refs.filter(ref => {
        const refCategories = (ref.category || []).map(normalizeCategory)
        return serviceCategories.some(serviceCat => 
          refCategories.some(refCat => 
            refCat.toLowerCase() === serviceCat.toLowerCase() ||
            refCat.toLowerCase().includes(serviceCat.toLowerCase()) ||
            serviceCat.toLowerCase().includes(refCat.toLowerCase())
          )
        )
      })

      // Maksimum 6 referans göster
      setReferencesData(filteredRefs.slice(0, 6))
    } catch (error) {
      console.error("Error loading service references:", error)
      setReferencesData([])
    } finally {
      setLoading(false)
    }
  }

  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: "start",
    slidesToScroll: 1,
  }, [Autoplay({ delay: 4000, stopOnInteraction: false })])
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((emblaApi: any) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  // Eğer referans yoksa component'i render etme (hooks'lardan sonra kontrol et)
  if (!loading && referencesData.length === 0) {
    return null
  }

  return (
    <Section className="relative bg-white py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden" containerClassName="!px-0">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" suppressHydrationWarning />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" suppressHydrationWarning />
      
      {/* Header - Container içinde */}
      <div className="relative z-10 mb-8 sm:mb-12 md:mb-16 px-4 sm:px-6 lg:px-8" suppressHydrationWarning>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-6 md:gap-8" suppressHydrationWarning>
          <div className="flex-1" suppressHydrationWarning>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block mb-2 sm:mb-3 md:mb-4"
              suppressHydrationWarning
            >
              <span className="text-xs sm:text-sm font-medium text-[rgb(3,74,166)] uppercase tracking-wider">
                {locale === 'en' ? 'Related Projects' : 'İlgili Projeler'}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-3 sm:mb-4 md:mb-6 leading-tight font-mono"
              suppressHydrationWarning
            >
              {locale === 'en' ? `${serviceName} References` : `${serviceName} Referanslarımız`}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed"
              suppressHydrationWarning
            >
              {locale === 'en' 
                ? `Examples of successful projects we have completed within the scope of ${serviceName.toLowerCase()}.`
                : `${serviceName} kapsamında gerçekleştirdiğimiz başarılı projelerden örnekler.`}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Top */}
      <div className="relative z-10 flex items-center mb-4 sm:mb-6 md:mb-8 px-4 sm:px-6 lg:px-8" suppressHydrationWarning>
        <div className="flex gap-2 sm:gap-3" suppressHydrationWarning>
          <button
            onClick={scrollPrev}
            disabled={prevBtnDisabled}
            className="p-2.5 sm:p-3 bg-slate-200/90 hover:bg-slate-300/90 active:bg-slate-400/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation"
            aria-label={locale === 'en' ? 'Previous' : 'Önceki'}
            suppressHydrationWarning
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-black stroke-[2]" />
          </button>
          <button
            onClick={scrollNext}
            disabled={nextBtnDisabled}
            className="p-2.5 sm:p-3 bg-slate-200/90 hover:bg-slate-300/90 active:bg-slate-400/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation"
            aria-label={locale === 'en' ? 'Next' : 'Sonraki'}
            suppressHydrationWarning
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-black stroke-[2]" />
          </button>
        </div>
      </div>

      {/* Carousel - Full Width */}
      <div className="relative z-10 w-full mb-6 sm:mb-8 md:mb-12 overflow-visible" suppressHydrationWarning>
        <div className="relative pl-4 sm:pl-6 lg:pl-8 pr-0" suppressHydrationWarning>
          <div className="overflow-hidden w-full md:w-[calc(100%+25%)] lg:w-[calc(100%+12.5%)]" ref={emblaRef} suppressHydrationWarning>
            <div className="flex gap-3 sm:gap-4" suppressHydrationWarning>
              {loading ? (
                <div className="flex-[0_0_100%] text-center py-8 sm:py-12 text-slate-500 text-sm sm:text-base" suppressHydrationWarning>
                  {locale === 'en' ? 'Loading...' : 'Yükleniyor...'}
                </div>
              ) : referencesData.length === 0 ? (
                <div className="flex-[0_0_100%] text-center py-8 sm:py-12 text-slate-500 text-sm sm:text-base" suppressHydrationWarning>
                  {locale === 'en' ? 'No references yet.' : 'Henüz referans bulunmuyor.'}
                </div>
              ) : (
                referencesData.map((ref) => (
                  <div
                    key={ref.id}
                    className="flex-[0_0_85%] sm:flex-[0_0_75%] md:flex-[0_0_50%] lg:flex-[0_0_28%] min-w-0 px-1 sm:px-2"
                    suppressHydrationWarning
                  >
                    <div className="group relative overflow-hidden bg-white cursor-pointer h-full" suppressHydrationWarning>
                      <Link href={`/${locale}/referanslar/${ref.slug || ref.id}`} className="block h-full">
                        {/* Image Container */}
                        <div className="relative aspect-[4/3] w-full overflow-hidden" suppressHydrationWarning>
                          {(() => {
                            // If no image URL, show placeholder
                            if (!ref.image || ref.image.trim() === "") {
                              return (
                                <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 flex items-center justify-center">
                                  <div className="text-center p-4">
                                    <div className="text-4xl mb-2">📷</div>
                                    <p className="text-xs text-slate-500 font-medium">Görsel Eklenecek</p>
                                  </div>
                                </div>
                              );
                            }
                            
                            const isVideo = /\.(webm|mp4|mov|avi|mkv)$/i.test(ref.image);
                            if (isVideo) {
                              return (
                                <video
                                  src={ref.image}
                                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                  muted
                                  loop
                                  playsInline
                                  preload="none"
                                />
                              );
                            }
                            return (
                              <Image
                                src={ref.image}
                                alt={ref.title}
                                fill
                                className="object-cover transition-transform duration-700 group-hover:scale-110"
                                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                loading="lazy"
                              />
                            );
                          })()}
                          
                          {/* Base Overlay - Always visible with site primary color */}
                          <div className="absolute inset-0 bg-gradient-to-t from-[rgb(3,74,166)]/60 via-[rgb(3,74,166)]/40 to-[rgb(3,74,166)]/20 transition-opacity duration-500" suppressHydrationWarning />
                          
                          {/* Overlay on Hover - Darker site primary color */}
                          <div className="absolute inset-0 bg-gradient-to-t from-[rgb(3,74,166)]/95 via-[rgb(3,74,166)]/70 to-[rgb(3,74,166)]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500" suppressHydrationWarning />
                          
                          {/* Title - Always visible at bottom, hides on hover */}
                          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6 bg-gradient-to-t from-[rgb(3,74,166)]/80 to-transparent group-hover:opacity-0 transition-opacity duration-500 z-10" suppressHydrationWarning>
                            <h3 className="text-base sm:text-lg md:text-xl font-bold text-white line-clamp-2">
                              {ref.title}
                            </h3>
                          </div>
                          
                          {/* Content Overlay - Shows on Hover/Touch */}
                          <div className="absolute inset-0 flex flex-col justify-end p-3 sm:p-4 md:p-6 text-white opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-500 z-20" suppressHydrationWarning>
                            <div className="space-y-2 sm:space-y-3 md:space-y-4" suppressHydrationWarning>
                              <h3 className="text-lg sm:text-xl md:text-2xl font-bold line-clamp-2">{ref.title}</h3>
                              <p className="text-xs sm:text-sm text-white/90 leading-relaxed line-clamp-2 sm:line-clamp-3">
                                {stripHTML(ref.description || '')}
                              </p>
                              <div className="flex items-center text-white font-medium transition-colors duration-300" suppressHydrationWarning>
                                <span className="text-xs sm:text-sm">
                                  {locale === 'en' ? 'View Case Study' : 'Vaka Çalışmasını İncele'}
                                </span>
                                <ArrowRight className="ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-300" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View All Button - Bottom */}
      <div className="relative z-10 flex justify-end items-center gap-2 text-slate-400 mt-6 sm:mt-8 md:mt-12 px-4 sm:px-6 lg:px-8" suppressHydrationWarning>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          suppressHydrationWarning
        >
          <Link href={`/${locale}/referanslar`} className="flex items-center gap-2 group hover:text-slate-600 active:text-slate-700 transition-colors touch-manipulation" suppressHydrationWarning>
            <span className="text-xs sm:text-sm font-medium">
              {locale === 'en' ? 'View All References' : 'Tüm Referansları Gör'}
            </span>
            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </Section>
  )
}

